import dayjs from 'dayjs';

import { formatDateTime } from './date';

/**
 * True when the API attached a Dawn/Dusk (or similar) label to the booking time.
 */
export function isDawnOrDuskLabel(formatted) {
  if (!formatted || typeof formatted !== 'string') return false;
  const lower = formatted.toLowerCase();
  return lower.includes('dawn') || lower.includes('dusk');
}

/**
 * If the API sent "Dusk - 24/04/2026" / "Dawn - 25/04/2026", show date first:
 * "24/04/2026 Dusk" so it matches the clock pattern (DD/MM/YYYY then time/label).
 */
export function normalizeDawnDuskLabelOrder(formatted) {
  if (!formatted || typeof formatted !== 'string') return formatted;
  const trimmed = formatted.trim();
  const m = trimmed.match(
    /^(Dawn|Dusk)\s*[-–]\s*(\d{1,2}\/\d{1,2}\/\d{4})\s*$/i
  );
  if (m) {
    const label = m[1].toLowerCase() === 'dawn' ? 'Dawn' : 'Dusk';
    return `${m[2]} ${label}`;
  }
  return formatted;
}

/**
 * "Dawn" when the lake uses dawn AM labels and this instant is before noon.
 */
export function shouldUseDawnForBooking(iso, dawnFlag) {
  if (!dawnFlag || !iso) return false;
  return dayjs(iso).hour() < 12;
}

/**
 * "Dusk" when the lake uses dusk PM labels and this instant is noon or later.
 */
export function shouldUseDuskForBooking(iso, duskFlag) {
  if (!duskFlag || !iso) return false;
  return dayjs(iso).hour() >= 12;
}

function readFlags(source) {
  return {
    dawnFlag: source?.dawnFlag ?? source?.dawn_as_am_label,
    duskFlag: source?.duskFlag ?? source?.dusk_as_pm_label,
  };
}

/**
 * Single precedence ladder used by every formatter:
 *   1. flag says Dawn  → render.dawn(iso)
 *   2. flag says Dusk  → render.dusk(iso)
 *   3. API label       → render.label(formatted, iso) (defaults to normalized label)
 *   4. no iso          → formatted || ''
 *   5. otherwise       → render.time(iso)
 *
 * Flag-first means a booking with both a flag and a formatted string renders
 * the same way across every surface.
 */
function formatBookingTime(iso, formatted, options) {
  const { dawnFlag, duskFlag } = readFlags(options);
  const render = options?.render || {};

  if (shouldUseDawnForBooking(iso, dawnFlag)) return render.dawn(iso);
  if (shouldUseDuskForBooking(iso, duskFlag)) return render.dusk(iso);

  if (isDawnOrDuskLabel(formatted)) {
    return render.label
      ? render.label(formatted, iso)
      : normalizeDawnDuskLabelOrder(formatted);
  }
  if (!iso) return formatted || '';
  return render.time(iso);
}

const dateSuffixPrint = (iso) => dayjs(iso).format('ddd. Do MMM ‘YY');

/**
 * Fishery admin tables — same style as formatDateTime(iso).
 */
export function adminFormatBookingDateTime(iso, formatted, options = {}) {
  return formatBookingTime(iso, formatted, {
    ...options,
    render: {
      dawn: (i) => `${dayjs(i).format('Do MMM.')} Dawn`,
      dusk: (i) => `${dayjs(i).format('Do MMM.')} Dusk`,
      time: (i) => formatDateTime(i),
    },
  });
}

/**
 * Fishery admin tables, compact "Departing" column — DD/MM + label/time.
 */
export function adminShortDepartureLine(iso, formatted, options = {}) {
  return formatBookingTime(iso, formatted, {
    ...options,
    render: {
      dawn: (i) => `${dayjs(i).format('DD/MM')} Dawn`,
      dusk: (i) => `${dayjs(i).format('DD/MM')} Dusk`,
      time: (i) => dayjs(i).format('ha - DD/MM'),
    },
  });
}

/**
 * Member profile → Bookings — DD/MM/YYYY HH:mm when not showing a label.
 */
export function memberProfileFormatBookingDateTime(iso, formatted, options = {}) {
  return formatBookingTime(iso, formatted, {
    ...options,
    render: {
      dawn: (i) => `${dayjs(i).format('DD/MM/YYYY')} Dawn`,
      dusk: (i) => `${dayjs(i).format('DD/MM/YYYY')} Dusk`,
      time: (i) => dayjs(i).format('DD/MM/YYYY HH:mm'),
    },
  });
}

/**
 * Fishery booking modal — "DD/MM/YYYY - h:mm A" or date + Dawn/Dusk.
 */
export function viewModalFormatArrivalDepartureLine(iso, formatted, options = {}) {
  return formatBookingTime(iso, formatted, {
    ...options,
    render: {
      dawn: (i) => `${dayjs(i).format('DD/MM/YYYY')} Dawn`,
      dusk: (i) => `${dayjs(i).format('DD/MM/YYYY')} Dusk`,
      time: (i) => `${dayjs(i).format('DD/MM/YYYY')} - ${dayjs(i).format('h:mm A')}`,
    },
  });
}

/**
 * Compact print/PDF check-in/out lines — "11am | Wed. 21st Apr '25"
 * or "Wed. 21st Apr '25 | dawn/dusk" when a label applies.
 */
export function printCheckInOutLine(iso, formatted, options = {}) {
  return formatBookingTime(iso, formatted, {
    ...options,
    render: {
      dawn: (i) => `${dateSuffixPrint(i)} | dawn`,
      dusk: (i) => `${dateSuffixPrint(i)} | dusk`,
      label: (f, i) => {
        if (!i) return normalizeDawnDuskLabelOrder(f);
        const word = f.split(/[-–]/)[0].trim().toLowerCase();
        return `${dateSuffixPrint(i)} | ${word}`;
      },
      time: (i) => `${dayjs(i).format('hA').toLowerCase()} | ${dateSuffixPrint(i)}`,
    },
  });
}

/**
 * Printable ticket "Expires:" line — dusk-only label (tickets never expire at dawn).
 */
export function printTicketExpiresLine(bookingData) {
  const iso = bookingData?.departure_timestamp;
  const formatted = bookingData?.departure_formatted;
  const duskFlag = bookingData?.dusk_as_pm_label;

  if (shouldUseDuskForBooking(iso, duskFlag)) {
    const fromApi = String(formatted || '').trim();
    if (fromApi) return normalizeDawnDuskLabelOrder(fromApi);
    return iso ? `${dayjs(iso).format('DD/MM/YYYY')} Dusk` : 'Dusk';
  }
  if (isDawnOrDuskLabel(formatted)) {
    return normalizeDawnDuskLabelOrder(formatted);
  }
  return dayjs(iso ?? '').format('DD/MM/YYYY h:mma');
}

const ticketSummaryRender = {
  dawn: (i) => `${dayjs(i).format('DD/MM/YYYY')} Dawn`,
  dusk: (i) => `${dayjs(i).format('DD/MM/YYYY')} Dusk`,
  // Ticket summary only renders when a label applies (see UserTicketSummary's
  // isArrivalFixed / isDepartureFixed guard), so this branch is a safety net:
  // echo whatever the API gave us rather than inventing a clock format.
  time: () => '',
};

/**
 * Post-booking ticket: arrival line.
 */
export function ticketSummaryArrivalLine(booking) {
  const iso = booking?.arrival_ts ?? booking?.arrival;
  const formatted = booking?.arrival_formatted;
  const result = formatBookingTime(iso, formatted, {
    dawnFlag: booking?.dawn_as_am_label,
    duskFlag: booking?.dusk_as_pm_label,
    render: ticketSummaryRender,
  });
  return result || formatted || '';
}

/**
 * Post-booking ticket: departure line.
 */
export function ticketSummaryDepartureLine(booking) {
  const iso = booking?.departure_ts ?? booking?.departure;
  const formatted = booking?.departure_formatted;
  const result = formatBookingTime(iso, formatted, {
    dawnFlag: booking?.dawn_as_am_label,
    duskFlag: booking?.dusk_as_pm_label,
    render: ticketSummaryRender,
  });
  return result || formatted || '';
}
